/**
 * App version / cache-busting signal.
 *
 * `VITE_APP_VERSION` is the git short SHA stamped into the build by CI (see
 * .github/workflows/*-member-360-standalone.yml). The identical value is emitted
 * to `/version.json` by the `emit-version-json` Vite plugin (see vite.config.ts).
 *
 * A running client compares the version it BOOTED with (`APP_VERSION`) against a
 * freshly-fetched `/version.json`. A difference means a newer build has been
 * deployed and this client is running stale code that references now-replaced
 * hashed bundles — so it should reload. See `useVersionCheck` / `VersionWatcher`.
 */
export const APP_VERSION: string = import.meta.env.VITE_APP_VERSION ?? "";

export interface VersionFile {
  version?: string;
}

/**
 * Pure decision: should a client running `current` reload to pick up `latest`?
 *
 * Returns true ONLY when both versions are known, non-empty, and differ. This
 * guards against reload loops and spurious reloads:
 *  - `latest` null/empty (unreadable signal, 404, malformed) → no reload.
 *  - `current` empty (unstamped local/dev build) → no reload.
 *  - equal versions → no reload.
 */
export function shouldReload(
  current: string | null | undefined,
  latest: string | null | undefined,
): boolean {
  if (!current || !latest) return false;
  return current !== latest;
}

/**
 * Fetch the deployed version signal from `/version.json`. Returns the version
 * string, or null on ANY failure (network error, non-200, malformed JSON,
 * missing/empty field) so callers fail closed (no reload).
 *
 * Uses `cache: "no-store"` so the check always reflects the live deploy even if
 * some intermediary ignores the `no-cache` response header nginx sets.
 */
export async function fetchLatestVersion(
  fetchImpl: typeof fetch = fetch,
): Promise<string | null> {
  try {
    const res = await fetchImpl("/version.json", { cache: "no-store" });
    if (!res.ok) return null;
    const body = (await res.json()) as VersionFile;
    const v = body?.version;
    return typeof v === "string" && v.length > 0 ? v : null;
  } catch {
    return null;
  }
}

/** Query param carrying the target version on a self-reload (stripped after boot). */
export const VERSION_PARAM = "_v";

/**
 * `href` with `?_v=<version>` set. A different URL is a different HTTP-cache
 * key, so the navigation cannot be satisfied from a stale cached document;
 * nginx's SPA fallback serves index.html for any query string.
 */
export function buildVersionedUrl(href: string, version: string): string {
  const url = new URL(href);
  url.searchParams.set(VERSION_PARAM, version);
  return url.toString();
}

/**
 * Path + query + hash with the version param removed, or null when the URL
 * carries none. Used after boot so the address stays clean.
 */
export function stripVersionParam(href: string): string | null {
  const url = new URL(href);
  if (!url.searchParams.has(VERSION_PARAM)) return null;
  url.searchParams.delete(VERSION_PARAM);
  return `${url.pathname}${url.search}${url.hash}`;
}
